import { DailySnapshot, HourlySnapshot } from '@/core/tokens';
import useVaults from '../vaults/useVaults';
import useLiquidityPools from './useLiquidityPools';
import useSnapshots from './useSnapshots';
import { Address } from 'viem';
import { useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../queryKeys';

const useVaultSnapshots = ({ vaultIds }: { vaultIds?: string[] }) => {
  const { data: pools } = useLiquidityPools({ vaultIds });
  const { data: vaults } = useVaults();
  const { data: snapshots } = useSnapshots(pools?.map((x) => x.id));

  // Explicitly define the type for poolSnapshots
  type PoolSnapshots = {
    [poolId: string]: {
      dailySnapshots: DailySnapshot[];
      hourlySnapshots: HourlySnapshot[];
      vaultId: string;
      tokenAddress: Address;
    };
  };

  return useQuery(
    [QueryKey.VAULT_SNAPSHOTS, vaultIds?.join(',')],
    () => {
      // Create an object keyed by poolId
      const poolSnapshots: PoolSnapshots = {};

      const allSnapshots = [
        ...(snapshots?.dailySnapshots || []),
        ...(snapshots?.hourlySnapshots || []),
      ];

      // Group dailySnapshots by poolId
      // Group both dailySnapshots and hourlySnapshots by poolId
      allSnapshots.forEach((snapshot) => {
        const { poolId } = snapshot;

        const vaultId = pools?.find((x) => x.id === poolId)?.vaultId;
        const vault = vaults?.find((v) => v.vaultId === vaultId);

        if (!vault || !vaultId) return;

        if (!poolSnapshots[vaultId]) {
          poolSnapshots[vaultId] = {
            dailySnapshots: [],
            hourlySnapshots: [],
            vaultId,
            tokenAddress: vault.id,
          };
        }
        if (snapshot.type === 'day') {
          poolSnapshots[vaultId].dailySnapshots.push(snapshot);
        } else {
          poolSnapshots[vaultId].hourlySnapshots.push(snapshot);
        }
      });

      return Object.values(poolSnapshots);
    },
    {
      enabled: !!snapshots && !!vaults,
    }
  );
};

export default useVaultSnapshots;
